import React, { Component, createRef } from "react";

// Polyfill-safe ResizeObserver
const ResizeObserver = window.ResizeObserver || class {
  observe() {}
  unobserve() {}
  disconnect() {}
};

/** Minimum average volume (0–255) to show oscillation; below this bars use minimal idle amplitude */
const VOLUME_THRESHOLD = 12;
/** Minimal bar height when silent (fraction of max) so waveform never looks dead */
const IDLE_AMPLITUDE_FRACTION = 0.04;

// Professional waveform design: Dark Mode + Neon Accent (blue–cyan gradient)
const DESIGN = {
  background: "#000000",
  baseline: "rgba(255, 255, 255, 0.18)",
  baselineDash: [4, 4],
  // Vertical gradient per bar: center (quiet) → peaks (loud) = darker → brighter
  colorCenter: "#2C4A7C",
  colorMid: "#5C9FD8",
  colorPeak: "#9FEFFF",
};

class AudioVisualizer extends Component {
  constructor(props) {
    super(props);
    this.canvasRef = createRef();
    this.audioContext = null;
    this.analyser = null;
    this.source = null;
    this.stream = null; // Only set for our own getUserMedia stream (so we stop it on unmount)
    this.rafId = null;
    this.frequencyData = null;
    this.bars = 128;
    this.lastDrawTime = 0;
    this.resizeObserver = null;
    this.scale = Math.min(window.devicePixelRatio || 1, 1.5);
  }

  componentDidMount() {
    this.setupAudio();
    const canvas = this.canvasRef.current;
    if (canvas) {
      this.resizeObserver = new ResizeObserver(() => this.handleResize());
      this.resizeObserver.observe(canvas);
      this.handleResize();
    }
    this.rafId = requestAnimationFrame(this.draw);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.remoteMediaStream !== this.props.remoteMediaStream) {
      this.teardownAudio();
      this.setupAudio();
      this.rafId = requestAnimationFrame(this.draw);
    }
  }

  componentWillUnmount() {
    this.teardownAudio();
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  teardownAudio = () => {
    cancelAnimationFrame(this.rafId);
    this.rafId = null;
    if (this.source) this.source.disconnect();
    this.source = null;
    this.analyser = null;
    this.frequencyData = null;
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
    if (this.audioContext && this.audioContext.state !== "closed") {
      this.audioContext.close();
    }
    this.audioContext = null;
  };

  handleResize = () => {
    const canvas = this.canvasRef.current;
    if (!canvas) return;
    const { width, height } = canvas.getBoundingClientRect();
    canvas.width = width * this.scale;
    canvas.height = height * this.scale;

    const ctx = canvas.getContext("2d");
    ctx.scale(this.scale, this.scale);
  };

  setupAudio = async () => {
    const remoteStream = this.props.remoteMediaStream;
    if (remoteStream && remoteStream.getAudioTracks().length > 0) {
      try {
        this.stream = null; // We don't own this stream; don't stop it on unmount
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        this.analyser = this.audioContext.createAnalyser();
        this.analyser.fftSize = 2048;
        this.analyser.smoothingTimeConstant = 0.8;
        this.source = this.audioContext.createMediaStreamSource(remoteStream);
        this.source.connect(this.analyser);
        this.frequencyData = new Uint8Array(this.analyser.frequencyBinCount);
      } catch (err) {
        console.warn("AudioVisualizer: failed to use remote stream", err);
      }
      return;
    }

    const isSecureContext = window.isSecureContext ||
      window.location.protocol === "https:" ||
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";
    if (!isSecureContext) {
      console.warn("Microphone access requires a secure connection (HTTPS).");
      return;
    }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.warn("Microphone access is not supported in this browser.");
      return;
    }
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 2048;
      this.analyser.smoothingTimeConstant = 0.8;
      this.source = this.audioContext.createMediaStreamSource(this.stream);
      this.source.connect(this.analyser);
      this.frequencyData = new Uint8Array(this.analyser.frequencyBinCount);
    } catch (err) {
      console.error("Microphone access denied or error.", err);
    }
  };

  draw = (timestamp) => {
    if (!this.lastDrawTime || timestamp - this.lastDrawTime > 16) {
      this.lastDrawTime = timestamp;
      this.renderBars();
    }
    this.rafId = requestAnimationFrame(this.draw);
  };

  renderBars = () => {
    const canvas = this.canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    if (width <= 0 || height <= 0) return;

    const centerY = height / 2;
    const barSpacing = width / this.bars;
    const barWidth = Math.max(1, barSpacing * 0.55);
    const maxHeight = height * 0.88;

    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = DESIGN.background;
    ctx.fillRect(0, 0, width, height);

    // Dotted center baseline (zero-amplitude reference)
    ctx.setLineDash(DESIGN.baselineDash);
    ctx.strokeStyle = DESIGN.baseline;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, centerY);
    ctx.lineTo(width, centerY);
    ctx.stroke();
    ctx.setLineDash([]);

    let scale = 1;
    let dataArray = null;
    if (this.analyser && this.frequencyData) {
      this.analyser.getByteFrequencyData(this.frequencyData);
      dataArray = this.frequencyData;
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) sum += dataArray[i];
      const average = dataArray.length ? sum / dataArray.length : 0;
      scale = average >= VOLUME_THRESHOLD ? 1 : Math.max(0, average / VOLUME_THRESHOLD);
    }

    const step = dataArray ? Math.max(1, Math.floor(dataArray.length / this.bars)) : 1;

    for (let i = 0; i < this.bars; i++) {
      const rawVal = dataArray ? dataArray[Math.min(i * step, dataArray.length - 1)] : undefined;
      const value = rawVal != null ? rawVal : 0;
      const rawAmplitude = dataArray ? (value / 255) * maxHeight * scale : 0;
      const amplitude =
        rawAmplitude < 2 ? Math.max(2, maxHeight * IDLE_AMPLITUDE_FRACTION) : rawAmplitude;
      const x = i * barSpacing + (barSpacing - barWidth) / 2;
      const top = centerY - amplitude / 2;
      const barHeight = amplitude;

      const gradient = ctx.createLinearGradient(x, top, x, top + barHeight);
      gradient.addColorStop(0, DESIGN.colorPeak);
      gradient.addColorStop(0.5, DESIGN.colorCenter);
      gradient.addColorStop(1, DESIGN.colorPeak);
      ctx.fillStyle = gradient;
      ctx.fillRect(x, top, barWidth, barHeight);
    }
  };

  render() {
    return (
      <canvas
        ref={this.canvasRef}
        style={{
          display: "block",
          borderRadius: "16px",
          background: DESIGN.background,
          border: "1px solid rgba(255, 255, 255, 0.12)",
          width: "100%",
          height: "100%",
          minHeight: "48px",
          maxWidth: "860px",
          margin: "0 auto",
        }}
      />
    );
  }
}

export default AudioVisualizer;






// WEBPACK FOOTER //
// ./src/components/common/AudioVisualizer.js